// @flow

import * as React from 'react';
import { Mutation } from 'react-apollo';
import Modal from 'react-bootstrap/lib/Modal';
import Button from 'react-bootstrap/lib/Button';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import FormControl from 'react-bootstrap/lib/FormControl';

import { deleteViewMutation, renameViewMutation } from '../../graphql/mutations/user';

import './ManageViewsModal.css';

type ViewSummary = {
  viewName: string,
  updatedAt?: string,
};

type Props = {
  show: boolean,
  onHide: () => void,
  moduleName: string,
  views: ViewSummary[],
  maxViews: number,
  currentViewName: ?string,
  onViewsChanged: () => void,
  onViewDeleted: (viewName: string) => void,
};

type State = {
  editingView: ?string,
  editName: string,
  deleteConfirm: ?string,
  isLoading: boolean,
};

class ManageViewsModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      editingView: null,
      editName: '',
      deleteConfirm: null,
      isLoading: false,
    };
  }

  handleStartEdit = (viewName: string) => {
    this.setState({
      editingView: viewName,
      editName: viewName,
      deleteConfirm: null,
    });
  };

  handleCancelEdit = () => {
    this.setState({
      editingView: null,
      editName: '',
    });
  };

  handleDeleteClick = (viewName: string) => {
    this.setState({
      deleteConfirm: viewName,
      editingView: null,
    });
  };

  render() {
    const { editingView, editName, deleteConfirm, isLoading } = this.state;
    const { props } = this;
    const viewsRemaining = props.maxViews - props.views.length;

    return (
      <Mutation mutation={deleteViewMutation}>
        {(deleteView: any) => (
          <Mutation mutation={renameViewMutation}>
            {(renameView: any) => {
              const handleSaveEdit = async () => {
                if (!editingView || !editName.trim()) return;

                if (editName.trim() === editingView) {
                  this.handleCancelEdit();
                  return;
                }

                this.setState({ isLoading: true });
                try {
                  const result = await renameView({
                    variables: {
                      moduleName: props.moduleName,
                      oldName: editingView,
                      newName: editName.trim(),
                    },
                  });

                  if (result.data.renameView.status) {
                    props.onViewsChanged();
                    this.handleCancelEdit();
                  } else {
                    alert(result.data.renameView.message || 'Failed to rename view');
                  }
                } catch (e) {
                  alert('Failed to rename view');
                }
                this.setState({ isLoading: false });
              };

              const handleConfirmDelete = async () => {
                if (!deleteConfirm) return;

                this.setState({ isLoading: true });
                try {
                  const result = await deleteView({
                    variables: {
                      moduleName: props.moduleName,
                      viewName: deleteConfirm,
                    },
                  });

                  if (result.data.deleteView.status) {
                    props.onViewsChanged();
                    props.onViewDeleted(deleteConfirm);
                    this.setState({ deleteConfirm: null });
                  } else {
                    alert(result.data.deleteView.message || 'Failed to delete view');
                  }
                } catch (e) {
                  alert('Failed to delete view');
                }
                this.setState({ isLoading: false });
              };

              return (
                <Modal show={props.show} onHide={props.onHide}>
                  <Modal.Header closeButton>
                    <Modal.Title>Manage Saved Views</Modal.Title>
                  </Modal.Header>

                  <Modal.Body>
                    {props.views.length === 0 ? (
                      <p className="text-muted">No saved views yet.</p>
                    ) : (
                      <div className="manage-views-list">
                        {props.views.map(view => (
                          <div key={view.viewName} className="manage-views-item">
                            {editingView === view.viewName ? (
                              // Editing mode
                              <div className="manage-views-edit-row">
                                <FormControl
                                  type="text"
                                  value={editName}
                                  onChange={(e) => this.setState({ editName: e.target.value })}
                                  maxLength={20}
                                  autoFocus
                                  disabled={isLoading}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleSaveEdit();
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') this.handleCancelEdit();
                                  }}
                                />
                                <Button
                                  bsSize="small"
                                  bsStyle="primary"
                                  onClick={handleSaveEdit}
                                  disabled={isLoading || !editName.trim()}
                                >
                                  <Glyphicon glyph="ok" />
                                </Button>
                                <Button
                                  bsSize="small"
                                  onClick={this.handleCancelEdit}
                                  disabled={isLoading}
                                >
                                  <Glyphicon glyph="remove" />
                                </Button>
                              </div>
                            ) : deleteConfirm === view.viewName ? (
                              // Delete confirmation mode
                              <div className="manage-views-delete-confirm">
                                <span>Delete "{view.viewName}"?</span>
                                <Button
                                  bsSize="small"
                                  bsStyle="danger"
                                  onClick={handleConfirmDelete}
                                  disabled={isLoading}
                                >
                                  {isLoading ? 'Deleting...' : 'Delete'}
                                </Button>
                                <Button
                                  bsSize="small"
                                  onClick={() => this.setState({ deleteConfirm: null })}
                                  disabled={isLoading}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              // Normal display mode
                              <React.Fragment>
                                <div className="manage-views-info">
                                  <Glyphicon glyph="list-alt" className="manage-views-icon" />
                                  <span className="manage-views-name">
                                    {view.viewName}
                                    {view.viewName === props.currentViewName && (
                                      <span className="manage-views-current">(current)</span>
                                    )}
                                  </span>
                                </div>
                                <div className="manage-views-actions">
                                  <Button
                                    bsSize="small"
                                    onClick={() => this.handleStartEdit(view.viewName)}
                                    title="Rename"
                                  >
                                    <Glyphicon glyph="pencil" />
                                  </Button>
                                  <Button
                                    bsSize="small"
                                    onClick={() => this.handleDeleteClick(view.viewName)}
                                    title="Delete"
                                  >
                                    <Glyphicon glyph="trash" />
                                  </Button>
                                </div>
                              </React.Fragment>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="manage-views-footer">
                      {viewsRemaining} of {props.maxViews} views remaining
                    </div>
                  </Modal.Body>

                  <Modal.Footer>
                    <Button onClick={props.onHide}>Close</Button>
                  </Modal.Footer>
                </Modal>
              );
            }}
          </Mutation>
        )}
      </Mutation>
    );
  }
}

export default ManageViewsModal;
